import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Calendar,
  Users,
  DollarSign,
  Palette,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  UserPlus,
  FileText,
  Bell,
  ArrowRight,
  Loader2,
  ClipboardList,
  CheckCheck,
  ExternalLink,
  ChevronRight,
  AlertTriangle,
  Eye,
} from 'lucide-react';
import { RootState } from '../store';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import { shouldSuppressErrorForAuth } from '../utils/authErrors';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { CURRENT_SHOP_CHANGED_EVENT } from '../utils/currentShop';
import { RefreshWidget } from '../components/RefreshWidget';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useRuntimeSettings } from '../hooks/useRuntimeSettings';
import {
  getTasks,
  getMyTasks,
  updateTask,
  Task,
  TASK_TYPE_LABELS,
  TASK_STATUS_LABELS,
} from '../services/tasksApi';

interface DashboardStats {
  appointmentsToday: number;
  totalClients: number;
  monthlyRevenue: number;
  activePractitioners: number;
}

interface RecentAppointment {
  id: string;
  clientId?: string;
  clientName: string;
  service: string;
  time: string;
  date: string;

  status:
    | 'confirmed'
    | 'pending'
    | 'completed'
    | 'scheduled'
    | 'checked_in'
    | 'in_progress';

  isMinor?: boolean;

  legalGuardian?: {
    firstName: string;
    lastName: string;
    phone?: string;
    email?: string;
    relationshipToMinor?: string;
  } | null;
}
interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
}

interface DashboardData {
  stats: DashboardStats;
  recentAppointments: RecentAppointment[];
  notifications: Notification[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  PIERCING_ASSIGNMENT: 'bg-violet-100 text-violet-700',
  DOCUMENT_REVIEW: 'bg-blue-100 text-blue-700',
  CONSENT_FORM: 'bg-amber-100 text-amber-700',
  DEPOSIT_FOLLOW_UP: 'bg-orange-100 text-orange-700',
  APPOINTMENT_PREPARATION: 'bg-teal-100 text-teal-700',
  PRACTITIONER_ONBOARDING: 'bg-green-100 text-green-700',
  CUSTOM: 'bg-gray-100 text-gray-600',
};

function isOverdue(endDate: string | null): boolean {
  if (!endDate) return false;
  return new Date(endDate).getTime() < Date.now();
}

function formatDeadline(endDate: string | null): string {
  if (!endDate) return '';
  const d = new Date(endDate);
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.setHours(0, 0, 0, 0)) / 86400000);
  if (diffDays < 0) return `En retard de ${Math.abs(diffDays)} j`;
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Demain';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

// ─── TasksWidget ──────────────────────────────────────────────────────────────

interface TasksWidgetProps {
  tasks: Task[];
  loading: boolean;
  isSuperAdmin: boolean;
  completingId: string | null;
  onComplete: (task: Task) => void;
  onNavigate: (url: string) => void;
  onViewAll: () => void;
}

const VISIBLE_LIMIT = 8;

function TasksWidget({
  tasks,
  loading,
  isSuperAdmin,
  completingId,
  onComplete,
  onNavigate,
  onViewAll,
}: TasksWidgetProps) {
  const [showAll, setShowAll] = useState(false);
  const overdueCount = tasks.filter((t) => isOverdue(t.endDate)).length;
  const displayed = showAll ? tasks : tasks.slice(0, VISIBLE_LIMIT);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-50">
            <ClipboardList className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {isSuperAdmin ? 'Toutes les tâches' : 'Mes tâches'}
            </h2>
            {!loading && (
              <p className="text-xs text-gray-500">
                {tasks.length === 0
                  ? 'Aucune tâche en cours'
                  : `${tasks.length} tâche${tasks.length > 1 ? 's' : ''} en cours`}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {overdueCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
              <AlertTriangle className="w-3.5 h-3.5" />
              {overdueCount} en retard
            </span>
          )}
          <button
            onClick={onViewAll}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-0.5"
          >
            Tout voir
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement des tâches…</span>
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <CheckCheck className="w-10 h-10 mb-3 text-green-400" />
          <p className="text-sm font-medium text-gray-600">Tout est à jour !</p>
          <p className="text-xs text-gray-400 mt-1">Aucune tâche en attente.</p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-gray-50">
            {displayed.map((task) => {
              const overdue = isOverdue(task.endDate);
              const deadline = formatDeadline(task.endDate);
              const isCompleting = completingId === task.id;

              return (
                <li
                  key={task.id}
                  className={`flex items-start gap-3 px-6 py-3.5 group hover:bg-gray-50 transition-colors ${
                    overdue ? 'bg-red-50/40 hover:bg-red-50' : ''
                  }`}
                >
                  {/* Status dot */}
                  <div className="mt-1 flex-shrink-0">
                    {task.status === 'IN_PROGRESS' ? (
                      <span className="block w-2.5 h-2.5 rounded-full bg-blue-500 ring-2 ring-blue-200" />
                    ) : overdue ? (
                      <span className="block w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-red-200" />
                    ) : (
                      <span className="block w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-amber-100" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium leading-snug truncate ${overdue ? 'text-red-800' : 'text-gray-800'}`}>
                        {task.title}
                      </p>
                      {/* Deadline pill */}
                      {deadline && (
                        <span
                          className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                            overdue
                              ? 'bg-red-100 text-red-700'
                              : deadline === "Aujourd'hui" || deadline === 'Demain'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {deadline}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${
                          TYPE_COLORS[task.taskType] ?? TYPE_COLORS.CUSTOM
                        }`}
                      >
                        {TASK_TYPE_LABELS[task.taskType]}
                      </span>
                      {isSuperAdmin && task.assignedTo && (
                        <span className="text-[11px] text-gray-400 truncate">
                          → {task.assignedTo.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {task.url && (
                      <button
                        onClick={() => onNavigate(task.url!)}
                        title="Ouvrir"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => onComplete(task)}
                      disabled={isCompleting}
                      title="Marquer comme terminé"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors disabled:opacity-40"
                    >
                      {isCompleting ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCheck className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Footer */}
          {tasks.length > VISIBLE_LIMIT && (
            <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                {showAll
                  ? 'Afficher moins'
                  : `Afficher ${tasks.length - VISIBLE_LIMIT} tâche${tasks.length - VISIBLE_LIMIT > 1 ? 's' : ''} de plus`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const role = String((user as any)?.role || '').toLowerCase().replace(/-/g, '_');
  const isSuperAdmin = role === 'super_admin';

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Tasks widget state ─────────────────────────────────────────
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const { needsShopSelection, canLoad, currentShopId, currentShop } = useShopScopedPage();
  const { autoRefreshIntervalSeconds } = useRuntimeSettings();

  const fetchTasks = useCallback(async (showLoader = false) => {
    if (showLoader) {
      setTasksLoading(true);
    }
    try {
      const data = isSuperAdmin
        ? await getTasks()
        : await getMyTasks();
      // Show only active tasks, overdue first then by endDate
      const active = data.filter((t) => t.status === 'PENDING' || t.status === 'IN_PROGRESS');
      active.sort((a, b) => {
        const now = Date.now();
        const aOver = a.endDate && new Date(a.endDate).getTime() < now;
        const bOver = b.endDate && new Date(b.endDate).getTime() < now;
        if (aOver && !bOver) return -1;
        if (!aOver && bOver) return 1;
        if (a.endDate && b.endDate) return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
        if (a.endDate) return -1;
        if (b.endDate) return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setTasks(active);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      if (showLoader) {
        setTasksLoading(false);
      }
    }
  }, [isSuperAdmin]);

  const handleCompleteTask = async (task: Task) => {
    setCompletingId(task.id);
    try {
      await updateTask(task.id, {
        status: 'DONE',
        effectiveEndDate: new Date().toISOString(),
      });
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (err) {
      console.error('Failed to complete task:', err);
    } finally {
      setCompletingId(null);
    }
  };

  const fetchDashboardData = useCallback(async (showInitialLoader = false) => {
    try {
      if (showInitialLoader) {
        setLoading(true);
      }
      setError(null);

      const response = await apiClient('/api/admin/dashboard');

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();

      if (data.success) {
        setDashboardData(data.data);
      } else {
        setError(data.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      if (shouldSuppressErrorForAuth(error)) return;
      setError('Failed to load dashboard data');
    } finally {
      if (showInitialLoader) {
        setLoading(false);
      }
    }
  }, []);

  const refreshDashboard = useCallback(async () => {
    await Promise.all([
      fetchDashboardData(false),
      fetchTasks(false),
    ]);
  }, [fetchDashboardData, fetchTasks]);

  const { refreshNow, refreshing, lastRefreshAt } = useAutoRefresh({
    intervalSeconds: autoRefreshIntervalSeconds,
    enabled: canLoad,
    onRefresh: refreshDashboard,
  });

  useEffect(() => {
    if (!canLoad) return;
    void fetchDashboardData(true);
    void fetchTasks(true);
  }, [canLoad, currentShopId, fetchDashboardData, fetchTasks]);

  useEffect(() => {
    if (!canLoad) return;
    const onChanged = () => {
      void refreshDashboard();
    };
    window.addEventListener(CURRENT_SHOP_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CURRENT_SHOP_CHANGED_EVENT, onChanged);
  }, [canLoad, currentShopId, refreshDashboard]);

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'confirmed':
      return <CheckCircle className="w-4 h-4 text-green-600" />;

    case 'scheduled':
      return <Calendar className="w-4 h-4 text-blue-600" />;

    case 'checked_in':
      return <Users className="w-4 h-4 text-purple-600" />;

    case 'in_progress':
      return <Clock className="w-4 h-4 text-orange-600" />;

    case 'pending':
      return <Clock className="w-4 h-4 text-yellow-600" />;

    case 'completed':
      return <CheckCircle className="w-4 h-4 text-blue-600" />;

    default:
      return <AlertCircle className="w-4 h-4 text-gray-600" />;
  }
};

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', label: t('dashboard.status.confirmed') },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('dashboard.status.pending') },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: t('dashboard.status.completed') }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.confirmed;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error}</span>
          </div>
          <button
            onClick={() => void refreshNow()}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    // e.g. session expired — SessionExpiredModal handles reconnection
    return null;
  }

  return (
    <div className="p-6 space-y-8">
      {/* Welcome Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-textPrimary mb-2 flex items-center space-x-2">
            <span>{t('dashboard.welcome', { name: user?.name || 'Admin' })}</span>
            <HelpIcon
              docPath="12-tableau-bord"
              tooltip="Documentation : Tableau de bord"
            />
          </h1>
          <p className="text-textSecondary">
            {t('dashboard.subtitle')}
            {currentShop?.name ? ` · ${currentShop.name}` : ''}
          </p>
        </div>

        <RefreshWidget
          intervalSeconds={autoRefreshIntervalSeconds}
          refreshing={refreshing}
          lastRefreshAt={lastRefreshAt}
          onRefresh={() => void refreshNow()}
          disabled={!canLoad}
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.appointmentsToday')}</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.appointmentsToday}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-sm text-green-600 font-medium">+12%</span>
            <span className="text-sm text-gray-500 ml-2">vs last week</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalClients')}</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.totalClients}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-sm text-green-600 font-medium">+8%</span>
            <span className="text-sm text-gray-500 ml-2">vs last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.monthlyRevenue')}</p>
              <p className="text-2xl font-bold text-gray-900">€{dashboardData.stats.monthlyRevenue.toLocaleString()}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-sm text-green-600 font-medium">+15%</span>
            <span className="text-sm text-gray-500 ml-2">vs last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.stats.activePractitioners')}</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.activePractitioners}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg">
              <Palette className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <span className="text-sm text-gray-500">All active</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/quick-client-create')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.quickAddClient')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.quickAddClientDescription')}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
              <UserPlus className="w-6 h-6 text-primary-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-primary-600">
            <span className="text-sm font-medium">{t('dashboard.getStarted')}</span>
            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        <button
          onClick={() => navigate('/planning/calendar')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.manageAppointments')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.manageAppointmentsDescription')}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600">
            <span className="text-sm font-medium">{t('dashboard.viewCalendar')}</span>
            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        <button
          onClick={() => navigate('/financial/invoices')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-left hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('dashboard.financialReports')}</h3>
              <p className="text-sm text-gray-600">{t('dashboard.financialReportsDescription')}</p>
            </div>
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-600">
            <span className="text-sm font-medium">{t('dashboard.viewReports')}</span>
            <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>
      </div>

      {/* My Tasks Widget */}
      <TasksWidget
        tasks={tasks}
        loading={tasksLoading}
        isSuperAdmin={isSuperAdmin}
        completingId={completingId}
        onComplete={handleCompleteTask}
        onNavigate={(url) => navigate(url)}
        onViewAll={() => navigate('/tasks')}
      />

      {/* Recent Appointments & Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Appointments */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recentAppointments')}</h2>
          </div>
          <div className="p-3 sm:p-6">
            {dashboardData.recentAppointments.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.recentAppointments.map((appointment) => (
  <div
    key={appointment.id}
    className={`rounded-lg border p-4 transition-colors ${
      appointment.isMinor
        ? 'border-amber-200 bg-amber-50/60'
        : 'border-transparent bg-gray-50'
    }`}
  >
    <div className="flex min-w-0 flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start">
        <div className="mt-1 shrink-0">
          {getStatusIcon(appointment.status)}
        </div>

        <div className="w-full min-w-0 flex-1 [overflow-wrap:anywhere]">
          {/* Nom + badge mineur */}
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900">
              {appointment.clientName}
            </p>

            {appointment.isMinor && (
              <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                Mineur
              </span>
            )}
          </div>

          <p className="mt-0.5 text-sm text-gray-600">
            {appointment.service}
          </p>

          {/* Informations tuteur */}
          {appointment.isMinor && (
            <div className="mt-3">
              {appointment.legalGuardian ? (
                <div className="w-full min-w-0 rounded-lg border border-amber-200 bg-white/80 px-3 py-2">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Users className="h-4 w-4 shrink-0 text-amber-700" />

                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                      Tuteur légal
                    </span>
                  </div>

                  <p className="text-sm font-semibold text-gray-900">
                    {appointment.legalGuardian.firstName}{' '}
                    {appointment.legalGuardian.lastName}
                  </p>

                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {appointment.legalGuardian.phone && (
                      <a
                        href={`tel:${appointment.legalGuardian.phone}`}
                        className="font-medium text-gray-600 hover:text-primary-600"
                      >
                        {appointment.legalGuardian.phone}
                      </a>
                    )}

                    {appointment.legalGuardian.email && (
                      <a
                        href={`mailto:${appointment.legalGuardian.email}`}
                        className="font-medium text-gray-600 hover:text-primary-600"
                      >
                        {appointment.legalGuardian.email}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Aucun tuteur légal renseigné
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Heure + statut */}
      <div className="min-w-0 text-left sm:shrink-0 sm:text-right">
       <p className="text-sm font-medium text-gray-900">
  {appointment.date} · {appointment.time}
</p>

        <div className="mt-1">
          {getStatusBadge(appointment.status)}
        </div>
        <button
  type="button"
  onClick={() => navigate(`/appointments/${appointment.id}`)}
  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700"
>
  <Eye className="w-4 h-4" />
  Voir le rendez-vous
</button>
      </div>
    </div>
  </div>
))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('dashboard.noAppointmentsToday')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('dashboard.scheduleIsClear')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.notifications')}</h2>
          </div>
          <div className="p-6">
            {dashboardData.notifications.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.notifications.map((notification) => (
                  <div key={notification.id} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{notification.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(notification.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bell className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('dashboard.noNotifications')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('dashboard.allCaughtUp')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}